import React, { useCallback, useMemo, useState } from 'react'
import type { ChartingTabsProps } from './ChartingTabs.types'
import { CHARTING_TABS_DEMO_ITEMS } from './ChartingTabs.types'
import './ChartingTabs.css'

const ChartingTabs: React.FC<ChartingTabsProps> = ({
  items = CHARTING_TABS_DEMO_ITEMS,
  value,
  defaultValue,
  disabled = false,
  className = '',
  darkMode = false,
  previewState,
  onChange,
}) => {
  const [internalValue, setInternalValue] = useState(
    defaultValue ?? items[0]?.id ?? '',
  )
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const isPreview = previewState != null

  const resolvedValue = useMemo(() => {
    if (previewState === 'selected') {
      return items[1]?.id ?? items[0]?.id ?? ''
    }
    if (previewState === 'default' || previewState === 'hover') {
      return items[0]?.id ?? ''
    }
    return value ?? internalValue
  }, [internalValue, items, previewState, value])

  const resolvedHoverId = useMemo(() => {
    if (previewState === 'hover') {
      return items[1]?.id ?? null
    }
    if (isPreview) {
      return null
    }
    return hoveredId
  }, [hoveredId, isPreview, items, previewState])

  const handleSelect = useCallback(
    (tabId: string) => {
      if (isPreview || disabled) {
        return
      }

      if (value === undefined) {
        setInternalValue(tabId)
      }

      onChange?.(tabId)
    },
    [disabled, isPreview, onChange, value],
  )

  const classNames = [
    'ds-charting-tabs',
    darkMode ? 'ds-charting-tabs--dark' : '',
    isPreview ? 'ds-charting-tabs--preview' : '',
    previewState ? `ds-charting-tabs--preview-${previewState}` : '',
    disabled ? 'ds-charting-tabs--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classNames}
      role="tablist"
      aria-orientation="horizontal"
      onMouseLeave={() => {
        if (!isPreview) {
          setHoveredId(null)
        }
      }}
    >
      {items.map((item) => {
        const isSelected = item.id === resolvedValue
        const isHovered = !isSelected && resolvedHoverId === item.id

        const tabClassNames = [
          'ds-charting-tabs__tab',
          isSelected
            ? 'ds-charting-tabs__tab--selected'
            : 'ds-charting-tabs__tab--default',
          isHovered ? 'ds-charting-tabs__tab--hover' : '',
        ]
          .filter(Boolean)
          .join(' ')

        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            className={tabClassNames}
            aria-selected={isSelected}
            disabled={disabled || isPreview}
            tabIndex={isPreview ? -1 : isSelected ? 0 : -1}
            onClick={() => handleSelect(item.id)}
            onMouseEnter={() => {
              if (!isPreview && !disabled) {
                setHoveredId(item.id)
              }
            }}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

export default ChartingTabs
